import { ATTR_NAMES } from '../types/attrNames';
import type { VerticalType } from '../types/cmsTypes';
import { PERSONAL_LOAN_ATTRIBUTES } from './providers/plCalculator';

/**
 * Where a calculated attribute's value comes from at render time.
 *
 * - `TI_QUOTE` — the live InsureMO travel quote the page (or the module) has
 *   already fetched, matched to a product by `INSUREMO_PRODUCT_UID`.
 * - `PL_CALCULATOR` — the `calculatePersonalLoans` batch (CMS-1921), matched to
 *   a product by the id the request was made with.
 */
export type CalculatedAttributeProvider = 'TI_QUOTE' | 'PL_CALCULATOR';

type CalculatedAttributeEntry = {
  provider: CalculatedAttributeProvider;
  /**
   * The vertical whose products these figures belong to. An attribute is
   * calculated only there: the nine loan names are ordinary catalogue
   * attributes on a credit card or a mortgage, with values an editor typed in
   * (CMS-1890 review).
   */
  vertical: VerticalType;
  /**
   * Whether the attribute has an authored value outside its vertical. A loan
   * figure does (an editor's APR on a card); the travel price does not — it
   * exists only as a quote, so outside its vertical there is nothing to show.
   */
  authoredElsewhere: boolean;
};

/**
 * One personal-loan entry, written through an annotated return type on purpose.
 * Spreading `Object.fromEntries` into the literal below erases the value check —
 * a misspelled vertical compiles, and since the lookup is one `===` against it,
 * every loan attribute would silently stop being calculated on every page, with
 * no test failing either (CMS-1890 review).
 */
const personalLoanEntry = (
  name: string,
): [string, CalculatedAttributeEntry] => [
  name,
  { provider: 'PL_CALCULATOR', vertical: 'PERSONAL_LOANS', authoredElsewhere: true },
];

/**
 * The registry of calculated attributes (CMS-1896): attributes that exist in
 * the Strapi dictionary so editors can select them anywhere an attribute picker
 * is offered, but whose VALUE is never authored — it is resolved at render time
 * from a provider's data. Membership here is the single source of truth for
 * "is this attribute calculated"; there is no marker in Strapi.
 */
const CALCULATED_ATTRIBUTES: Partial<Record<string, CalculatedAttributeEntry>> =
  {
    [ATTR_NAMES.TI_PREMIUM_PRICE]: {
      provider: 'TI_QUOTE',
      vertical: 'TRAVEL_INSURANCE',
      authoredElsewhere: false,
    },
    // The personal-loan set is spread from its provider's own list rather than
    // repeated here: `plCalculator.ts` has to name each attribute anyway
    // to say which calculation field feeds it, and two hand-kept lists would
    // drift. Membership here is still what makes an attribute "calculated".
    ...Object.fromEntries(PERSONAL_LOAN_ATTRIBUTES.map(personalLoanEntry)),
  };

/**
 * Which provider serves this attribute on a page of this vertical, or
 * `undefined` when the attribute is an ordinary catalogue one there.
 *
 * This is the engine's one judgement, and it is deliberately the only place
 * that makes it: a block reports the attributes it draws and, where it knows
 * one, the vertical it is authored for — it does not decide which of them are
 * calculated (CMS-1890 review, round 3).
 */
export const getCalculatedAttributeProviderIn = (
  attributeName: string | undefined | null,
  vertical: VerticalType | undefined | null,
): CalculatedAttributeProvider | undefined => {
  const entry = attributeName
    ? CALCULATED_ATTRIBUTES[attributeName]
    : undefined;
  return entry && entry.vertical === vertical ? entry.provider : undefined;
};

/**
 * Whether this attribute is calculated in ANY vertical. The render side's
 * question: a cell knows the provider's data is at hand or is not, and the
 * vertical was already decided when the request was collected — asking for it
 * again in every cell would thread the page through the whole render tree.
 */
export const getCalculatedAttributeProvider = (
  attributeName?: string,
): CalculatedAttributeProvider | undefined =>
  attributeName ? CALCULATED_ATTRIBUTES[attributeName]?.provider : undefined;

/** Whether the attribute has an authored value outside its own vertical. */
export const isAuthoredElsewhere = (attributeName?: string): boolean =>
  !!attributeName && !!CALCULATED_ATTRIBUTES[attributeName]?.authoredElsewhere;
